import { SCENE_VERSION_V1, SCENE_VERSION_V2 } from "./versions.js";
const DEFAULT_STROKE = "#1a1a2e";
const DEFAULT_STICKY_BG = "#fef08a";
function asRecord(value) {
    if (value && typeof value === "object" && !Array.isArray(value)) {
        return value;
    }
    return null;
}
function num(value) {
    return typeof value === "number" && Number.isFinite(value) ? value : undefined;
}
function str(value) {
    return typeof value === "string" ? value : undefined;
}
function bool(value) {
    return typeof value === "boolean" ? value : undefined;
}
/** Объект v1: нет вложенного `style`, но есть legacy-поля на верхнем уровне. */
export function isV1ShapedObject(raw) {
    const o = asRecord(raw);
    if (!o || o.style !== undefined)
        return false;
    const type = o.type;
    if (type === "sticky" && o.color !== undefined)
        return true;
    if (type === "rect" && (o.fill !== undefined || o.stroke !== undefined))
        return true;
    if (type === "path" && (o.stroke !== undefined || o.strokeWidth !== undefined))
        return true;
    if (type === "image")
        return true;
    return false;
}
function baseFromLegacy(o) {
    return {
        type: String(o.type),
        id: String(o.id),
        x: num(o.x) ?? 0,
        y: num(o.y) ?? 0,
        w: num(o.w),
        h: num(o.h),
        rotation: num(o.rotation),
        zIndex: str(o.zIndex),
        locked: bool(o.locked),
        opacity: num(o.opacity),
        documentId: str(o.documentId),
        documentPage: num(o.documentPage),
        meta: asRecord(o.meta) ?? undefined,
    };
}
function migrateSticky(o) {
    const base = baseFromLegacy(o);
    const style = {
        backgroundColor: str(o.color) ?? DEFAULT_STICKY_BG,
        color: DEFAULT_STROKE,
    };
    return {
        type: "sticky",
        id: base.id,
        x: base.x,
        y: base.y,
        w: base.w ?? 200,
        h: base.h ?? 200,
        rotation: base.rotation,
        zIndex: base.zIndex,
        locked: base.locked,
        opacity: base.opacity,
        documentId: base.documentId,
        documentPage: base.documentPage,
        meta: base.meta,
        text: str(o.text),
        style,
    };
}
function migrateRect(o) {
    const base = baseFromLegacy(o);
    const style = {
        fill: str(o.fill) ?? "none",
        stroke: str(o.stroke) ?? DEFAULT_STROKE,
        strokeWidth: 2,
    };
    return {
        type: "rect",
        id: base.id,
        x: base.x,
        y: base.y,
        w: base.w ?? 100,
        h: base.h ?? 100,
        rotation: base.rotation,
        zIndex: base.zIndex,
        locked: base.locked,
        opacity: base.opacity,
        documentId: base.documentId,
        documentPage: base.documentPage,
        meta: base.meta,
        style,
    };
}
function migrateImage(o) {
    const base = baseFromLegacy(o);
    const assetId = str(o.assetId);
    if (!assetId) {
        throw new Error("image object missing assetId");
    }
    return {
        type: "image",
        id: base.id,
        x: base.x,
        y: base.y,
        w: base.w ?? 100,
        h: base.h ?? 100,
        rotation: base.rotation,
        zIndex: base.zIndex,
        locked: base.locked,
        opacity: base.opacity,
        documentId: base.documentId,
        documentPage: base.documentPage,
        meta: base.meta,
        assetId,
        style: {},
    };
}
function migratePath(o) {
    const base = baseFromLegacy(o);
    const pointsRaw = o.points;
    if (!Array.isArray(pointsRaw) || pointsRaw.length < 2) {
        throw new Error("path object invalid points");
    }
    const points = pointsRaw.map((p) => {
        if (!Array.isArray(p) || p.length < 2)
            throw new Error("path point invalid");
        return [num(p[0]) ?? 0, num(p[1]) ?? 0];
    });
    const style = {
        stroke: str(o.stroke) ?? DEFAULT_STROKE,
        strokeWidth: num(o.strokeWidth) ?? 3,
        strokeOpacity: num(o.strokeOpacity),
        strokeLinecap: str(o.strokeLinecap),
        strokeLinejoin: str(o.strokeLinejoin),
        strokeDash: str(o.strokeDash),
    };
    return {
        type: "path",
        id: base.id,
        x: base.x,
        y: base.y,
        w: base.w ?? 1,
        h: base.h ?? 1,
        rotation: base.rotation,
        zIndex: base.zIndex,
        locked: base.locked,
        opacity: base.opacity,
        documentId: base.documentId,
        documentPage: base.documentPage,
        meta: base.meta,
        points,
        style,
    };
}
const V2_PASSTHROUGH_TYPES = new Set([
    "sticky",
    "text",
    "rect",
    "ellipse",
    "triangle",
    "diamond",
    "star",
    "geom",
    "image",
    "audio",
    "path",
    "line",
    "arrow",
    "pdf_document",
    "timer",
    "calculator",
    "code",
    "sql",
    "frame",
]);
/** Нормализует один объект v1 или v2 → BoardObjectV2. */
export function migrateBoardObject(raw) {
    const o = asRecord(raw);
    if (!o || !o.type || !o.id)
        return null;
    if (!isV1ShapedObject(o)) {
        const type = String(o.type);
        if (V2_PASSTHROUGH_TYPES.has(type)) {
            return o;
        }
        return null;
    }
    switch (o.type) {
        case "sticky":
            return migrateSticky(o);
        case "rect":
            return migrateRect(o);
        case "image":
            try {
                return migrateImage(o);
            }
            catch {
                return null;
            }
        case "path":
            try {
                return migratePath(o);
            }
            catch {
                return null;
            }
        default:
            return null;
    }
}
export function migrateSceneV1ToV2(raw) {
    const objects = [];
    for (const item of raw.objects) {
        try {
            const migrated = migrateBoardObject(item);
            if (migrated)
                objects.push(migrated);
        }
        catch {
            /* skip invalid legacy object */
        }
    }
    return { version: SCENE_VERSION_V2, objects };
}
/** Принимает v1 или v2 JSON → канонический Scene v2. */
export function normalizeScene(raw) {
    const o = asRecord(raw);
    if (!o || !Array.isArray(o.objects)) {
        return { version: SCENE_VERSION_V2, objects: [] };
    }
    if (o.version === SCENE_VERSION_V1) {
        return migrateSceneV1ToV2({ version: SCENE_VERSION_V1, objects: o.objects });
    }
    if (o.version === SCENE_VERSION_V2) {
        const objects = [];
        for (const item of o.objects) {
            try {
                const migrated = migrateBoardObject(item);
                if (migrated)
                    objects.push(migrated);
            }
            catch {
                /* skip invalid object */
            }
        }
        return { version: SCENE_VERSION_V2, objects };
    }
    return { version: SCENE_VERSION_V2, objects: [] };
}
export function emptySceneV2() {
    return { version: SCENE_VERSION_V2, objects: [] };
}
